import React, { useRef, useEffect, useState, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Select,
  Button,
  Pagination,
  Spin,
} from "antd";
import LeadCard from "./LeadCard";
import AdminCard from "./AdminCard";
import {
  FilterOutlined,
  UserOutlined,
  ProfileOutlined,
  ShoppingCartOutlined,
  CheckOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import "./newDashboard.css";
import useLeadOptions from "../../components/NewDashboard/UseLeadOptions";
import axiosNew from "../../store/api/axiosNew";
import useBusinessDashboardApi from "../../hooks/useBusinessDashboardApi";
import NotificationPopup from "../../components/NotificationPopup";
import { formatAmountIndian } from "./FormatAmount";

const NewUserDashboardWeb = ({ navState }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { Title } = Typography;
  const dispatch = useDispatch();

  // --- 1. State Management ---
  const [loadingNav, setLoadingNav] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadNoti, setLeadNoti] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [rightColPos, setRightColPos] = useState({ left: 0, width: 0 });
  // This state stores results specifically from the Filter Page API hit
  const [filteredLeadsNav, setFilteredLeadsNav] = useState([]);
  const [lastSearchParams, setLastSearchParams] = useState({});
  const [subcategoryLeadsMap, setSubcategoryLeadsMap] = useState({});
  const [subcategoryLoading, setSubcategoryLoading] = useState({});
  const [locations, setLocations] = useState([]);
  const [landTypes, setLandTypes] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [isHoveredDetail, setIsHoveredDetail] = useState(false);
  const [filters, setFilters] = useState({
    location: [],
    landType: [],
    budget: [],
    plotSize: [],
  });

  const rightColRef = useRef(null);
  const userName = localStorage.getItem("name");

  // --- 2. Data Source Extraction ---
  const preFetchedLeads = navState?.results || location.state?.results || [];
  const filterLeadsFromNav = navState?.filters || location.state?.filters;

  // --- 3. Custom Hook API (Main Data Source) ---
  const {
    allLeads,
    pagination,
    cartLeads,
    loading,
    fetchAllLeads,
    updateLeadCategory,
    addLeadToCart,
    removeLeadFromCart,
  } = useBusinessDashboardApi();

  // --- 4. Data Transformation Helper ---
  const transformLeadData = (lead) => ({
    id: lead._id || lead.id,
    name: lead.name,
    place: lead.location,
    price: lead.budget,
    currentRate: lead.currentRate,
    landType: lead.subcategory?.label || "",
    rating: `${lead.timesBought || 0}/${lead.leadAvailableCount || 0}`,
    status: lead.status || "",
    siteVisit: lead.siteVisitDays?.join(", ") || "",
    lookingFor: lead.property_details || "",
    contact: lead.phone,
    type: lead.leadType,
    category: lead.category,
    sqft: lead.spaceSqft || 0,
    subCategoryId: lead.subcategory?._id,
    subCategoryLabel: lead.subcategory?.label || "",
    subCategoryName: lead.subcategory?.name || "",
    state: lead.state || "",
    district: lead.district || "",
    area: lead.area || "",
    pincode: lead.pincode || "",
  });

  const fetchCommonOptions = async (field, setter) => {
    try {
      const res = await axiosNew.get("admin/publicleads/common", {
        params: { field },
      });

      const options = (res.data?.data || []).map((item) => {
        const val = item.value; // don’t use .trim(), keep numbers as-is
        const label =
          typeof val === "number"
            ? `${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(val)}`
            : `${val?.trim()}`; // only trim strings
        return {
          value: val,
          label: label,
        };
      });

      setter(options);
    } catch (err) {
      console.error(`Failed to load ${field}`, err);
    }
  };

  useEffect(() => {
    fetchCommonOptions("location", setLocations);
    fetchCommonOptions("landType", setLandTypes);
    fetchCommonOptions("budget", setBudgets);
  }, []);

  // --- 5. Computed Leads Logic (THE FIX) ---
  const { hookLeads, finalLeads } = useMemo(() => {
    const baseLeads = Array.isArray(allLeads)
      ? allLeads.map(transformLeadData)
      : [];

    const navLeads = Array.isArray(filteredLeadsNav)
      ? filteredLeadsNav.map(transformLeadData)
      : [];

    return {
      hookLeads: baseLeads,
      finalLeads: navLeads.length > 0 ? navLeads : baseLeads,
    };
  }, [allLeads, filteredLeadsNav]);

  // --- 6. External Filtering Logic ---
  const fetchFilteredLeadsFromPage = async (navFilters) => {
    setLoadingNav(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const baseURL = "https://apilead.blaccdot.com/api/admin/all-leads";

      const params = {};
      if (navFilters?.area) params.search = navFilters.area;
      if (navFilters?.landType?.length > 0)
        params.category = navFilters.landType.join(",");
      if (navFilters?.budget?.length === 2) {
        params.minBudget = navFilters.budget[0];
        params.maxBudget = navFilters.budget[1];
      }
      if (navFilters?.plotSize?.length > 0)
        params.plotSize = navFilters.plotSize.join(",");
      if (navFilters?.state) params.state = navFilters.state;
      if (navFilters?.district) params.district = navFilters.district;

      const res = await axiosNew.get(baseURL, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params,
      });

      const data = res.data?.data || [];

      setFilteredLeadsNav([...data]);
    } catch (err) {
      console.error("Error fetching filtered leads:", err);
      setFilteredLeadsNav([]);
    } finally {
      setLoadingNav(false);
    }
  };
  const getSelectedLeadIdFromURL = () => {
    const params = new URLSearchParams(location.search);
    return params.get("selectedleadid"); // returns the string ID or null
  };

  const selectedLeadIdFromURL = new URLSearchParams(location.search).get(
    "selectedleadid",
  );

  useEffect(() => {
    if (!selectedLeadIdFromURL) return;

    fetchAllLeads(1, {
      ...lastSearchParams,
      leadId: selectedLeadIdFromURL,
    });
  }, [selectedLeadIdFromURL]);

  useEffect(() => {
    // If preFetchedLeads is the object from your JSON, we need the .data array
    const leadsToSet = Array.isArray(preFetchedLeads)
      ? preFetchedLeads
      : preFetchedLeads?.data || [];

    if (leadsToSet.length > 0) {
      setFilteredLeadsNav(leadsToSet);
    }
  }, [preFetchedLeads]);

  useEffect(() => {
    const leadIdFromURL = getSelectedLeadIdFromURL();
    if (leadIdFromURL && finalLeads.length > 0) {
      // alert(`Selected lead ID from URL: ${leadIdFromURL}`);
      const leadToSelect = finalLeads.find((lead) => lead.id === leadIdFromURL);
      if (leadToSelect) {
        setSelectedLead(leadToSelect);
      }
    }
  }, [finalLeads]);

  // --- 7. Notification & UI Layout Effects ---
  useEffect(() => {
    const userId = localStorage.getItem("id");
    const hasSeenPopup = sessionStorage.getItem("hasSeenPopup");
    const notificationNewLead = async () => {
      try {
        const res = await axiosNew.get(`/notification/${userId}/unread`);
        if (res.data.success && Array.isArray(res.data.data)) {
          if (!hasSeenPopup) {
            setLeadNoti(res.data.data);
            setShowPopup(true);
            sessionStorage.setItem("hasSeenPopup", "true");
          }
        }
      } catch (err) {
        console.error(err);
      }
    };
    notificationNewLead();
  }, []);

  useEffect(() => {
    const updatePosition = () => {
      if (rightColRef.current) {
        const rect = rightColRef.current.getBoundingClientRect();
        setRightColPos({ left: rect.left, width: rect.width });
      }
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, []);

  useEffect(() => {
    const leadIdFromURL = getSelectedLeadIdFromURL();
    if (leadIdFromURL && finalLeads.length > 0) {
      // alert(`Selected lead ID from URL: ${leadIdFromURL}`);
      const leadToSelect = finalLeads.find((lead) => lead.id === leadIdFromURL);
      if (leadToSelect) {
        setSelectedLead(leadToSelect);
      }
    }
  }, [finalLeads]);
  // --- 8. Options & Handlers ---
  // const { locationOptions, landTypeOptions, budgetOptions, plotSizeOptions } =
  //   useLeadOptions(hookLeads);

  const subCategoryOptions = [
    { label: "Plot", value: "plot" },
    { label: "Villa", value: "villa" },
    { label: "Apartment", value: "apartment" },
    { label: "Office", value: "office" },
    { label: "Shop", value: "shop" },
  ];

  const handleLeadCheck = (lead, isChecked) => {
    isChecked ? addLeadToCart(lead) : removeLeadFromCart(lead.id);
  };

  const handleSearch = () => {
    const params = {};

    if (filters.location?.length) {
      params.location = filters.location.join(",");
    }

    if (filters.landType?.length) {
      params.landType = filters.landType.join(",");
    }

    if (filters.budget?.length) {
      params.budget = filters.budget.join(",");
    }

    setLastSearchParams(params);
    fetchAllLeads(1, params);
  };

  const handleClearSearchFilter = () => {
    // 1. Reset local dashboard search dropdowns
    setFilters({ location: [], landType: [], budget: [], plotSize: [] });
    setLastSearchParams({});
    fetchAllLeads(1, {});

    // 2. Clear the pre-fetched results from the filter page
    setFilteredLeadsNav([]);

    // 3. Clear the router state so the data doesn't come back on refresh
    navigate(location.pathname, { replace: true, state: {} });
  };

  const adminLead =
    hookLeads[Math.floor(Math.random() * hookLeads.length)] || {};

  const fetchLeadsBySubcategory = async (subcat) => {
    setSubcategoryLoading((p) => ({ ...p, [subcat]: true }));

    try {
      const res = await axiosNew.get(`admin/publicleads/subcategory/${subcat}`);

      const leads = Array.isArray(res.data?.data)
        ? res.data.data.map(transformLeadData)
        : [];

      setSubcategoryLeadsMap((prev) => ({
        ...prev,
        [subcat]: leads,
      }));
    } catch (err) {
      console.error(`Failed to fetch ${subcat} leads`, err);
      setSubcategoryLeadsMap((prev) => ({
        ...prev,
        [subcat]: [],
      }));
    } finally {
      setSubcategoryLoading((p) => ({ ...p, [subcat]: false }));
    }
  };
  useEffect(() => {
    subCategoryOptions.forEach((sc) => {
      fetchLeadsBySubcategory(sc.value);
    });
  }, []);

  //to arrange them in descending order acc to their length
  const sortedSubCategories = useMemo(() => {
    return [...subCategoryOptions].sort((a, b) => {
      const aLen = subcategoryLeadsMap[a.value]?.length || 0;
      const bLen = subcategoryLeadsMap[b.value]?.length || 0;
      return bLen - aLen; // descending
    });
  }, [subCategoryOptions, subcategoryLeadsMap]);

  return (
    <div style={{ padding: "4px", fontSize: "13px" }}>
      {/* Header */}
      <div
        style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 12px",
            background: "#fff",
            borderRadius: 8,
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            border: "1px solid #f0f0f0",
          }}
        >
          <UserOutlined style={{ color: "#1890ff" }} />
          <Title strong style={{ fontSize: 15, margin: 0 }}>
            {userName}
          </Title>
        </div>
      </div>

      {/* Filter Row */}
      <Row gutter={16} align="middle" style={{ marginBottom: "16px" }}>
        <Col span={6}>
          <Title level={4}>All Leads</Title>
        </Col>
        {leadNoti.length > 0 && (
          <NotificationPopup
            show={showPopup}
            handleClose={() => setShowPopup(false)}
            lead={leadNoti[0]}
          />
        )}

        <Col span={13}>
          <div
            style={{
              display: "flex",
              border: "1px solid #DBEAFF",
              borderRadius: 6,
              background: "#fff",
            }}
          >
            <Select
              mode="multiple"
              className="custom-select"
              placeholder="Location"
              style={{ flex: 1 }}
              options={locations}
              value={filters.location}
              onChange={(values) =>
                setFilters((prev) => ({ ...prev, location: values }))
              }
              // onChange={(v) => setFilters((p) => ({ ...p, location: v }))}
            />
            <Select
              mode="multiple"
              className="custom-select"
              placeholder="Type"
              style={{ flex: 1 }}
              options={landTypes}
              value={filters.landType}
              onChange={(values) =>
                setFilters((prev) => ({ ...prev, landType: values }))
              }
              // onChange={(v) => setFilters((p) => ({ ...p, landType: v }))}
            />
            <Select
              mode="multiple"
              className="custom-select"
              placeholder="Budget"
              style={{ flex: 1 }}
              options={budgets}
              value={filters.budget}
              onChange={(values) =>
                setFilters((prev) => ({ ...prev, budget: values }))
              }
              // onChange={(v) => setFilters((p) => ({ ...p, budget: v }))}
            />
            <Button
              type="primary"
              style={{ borderRadius: 0 }}
              onClick={handleSearch}
            >
              Search
            </Button>
          </div>
        </Col>
        <Col span={5}>
          <Button
            danger
            onClick={handleClearSearchFilter}
            style={{ marginRight: 8 }}
          >
            Clear
          </Button>
          <Button
            icon={<ProfileOutlined />}
            onClick={() => navigate("/user-dashboard-filter")}
          >
            Notify leads
          </Button>
        </Col>
      </Row>

      {/* Main Content */}
      <Row gutter={20}>
        <Col span={6}>
          {loadingNav ? (
            <Spin size="small" />
          ) : (
            finalLeads.map((lead) => (
              <LeadCard
                key={lead.id}
                {...lead}
                showCheckbox={true}
                checked={cartLeads.some((l) => l.id === lead.id)}
                onCheck={(id, checked) => handleLeadCheck(lead, checked)}
                onClick={() => setSelectedLead(lead)}
                isSelected={selectedLead?.id === lead.id}
              />
            ))
          )}
          <Pagination
            simple
            showSizeChanger={false}
            current={pagination.page}
            total={pagination.total || 10}
            pageSize={pagination.limit}
            onChange={(page) => fetchAllLeads(page, lastSearchParams)}
            style={{ marginTop: 16 }}
          />
        </Col>

        <Col flex="2px">
          <div
            style={{ width: 2, backgroundColor: "#DBEAFF", height: "100%" }}
          />
        </Col>

        <Col span={17} ref={rightColRef}>
          {selectedLead ? (
            <>
              {/* <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <Typography.Title level={4} style={{ margin: 0 }}>
                  Lead Details
                </Typography.Title>
                <Select
                  placeholder="Update Lead Status"
                  value={selectedLead?.subCategoryName}
                  style={{ width: 220 }}
                  options={subCategoryOptions}
                  onChange={(subCategoryName, option) => {
                    // if (!selectedLead) return;

                    updateLeadCategory(
                      selectedLead.id,
                      subCategoryName,
                      option.label,
                    );
                    // 2️⃣ Update right-side panel immediately
                    setSelectedLead((prev) => ({
                      ...prev,
                      subCategoryName, // "villa"
                      subCategoryLabel: option.label,
                    }));
                  }}
                />
              </div> */}
              <Card
                style={{
                  borderRadius: 6,
                  background: "#fafafa",
                  marginBottom: 12,
                }}
              >
                <Row gutter={0} style={{ flexWrap: "wrap" }}>
                  <Col
                    span={6}
                    className="search-select-divider col-padding"
                    style={{ minWidth: 0 }}
                  >
                    <strong>Name</strong>
                    <p style={{ wordBreak: "break-word", margin: 0 }}>
                      {selectedLead.name}
                    </p>
                  </Col>
                  <Col
                    span={6}
                    className="search-select-divider col-padding"
                    style={{ minWidth: 0 }}
                  >
                    <strong>Contact No.</strong>
                    <p style={{ wordBreak: "break-word", margin: 0 }}>
                      {selectedLead.contact}
                    </p>
                  </Col>
                  <Col
                    span={6}
                    className="search-select-divider col-padding"
                    style={{ minWidth: 0 }}
                  >
                    <strong>Client Location</strong>
                    <p style={{ wordBreak: "break-word", margin: 0 }}>
                      {selectedLead.place}
                    </p>
                  </Col>
                  <Col span={6} className="col-padding" style={{ minWidth: 0 }}>
                    <strong>Sq.ft</strong>
                    <p style={{ wordBreak: "break-word", margin: 0 }}>
                      {selectedLead.sqft}
                    </p>
                  </Col>
                </Row>

                <Row gutter={0} style={{ marginTop: 20, flexWrap: "wrap" }}>
                  <Col
                    span={6}
                    className="search-select-divider col-padding"
                    style={{ minWidth: 0 }}
                  >
                    <strong>Budget</strong>
                    <p style={{ wordBreak: "break-word", margin: 0 }}>
                      ₹ {formatAmountIndian(selectedLead.price)}
                    </p>
                  </Col>
                  <Col
                    span={6}
                    className="search-select-divider col-padding"
                    style={{ minWidth: 0 }}
                  >
                    <strong>Preferred location</strong>
                    <p style={{ wordBreak: "break-word", margin: 0 }}>
                      {[
                        selectedLead?.state,
                        selectedLead?.district,
                        selectedLead?.area,
                        selectedLead?.pincode,
                      ]
                        .filter(Boolean)
                        .join(" - ")}
                    </p>
                  </Col>
                  <Col
                    span={6}
                    className="search-select-divider col-padding"
                    style={{ minWidth: 0 }}
                  >
                    <strong>Site Visit</strong>
                    <p style={{ wordBreak: "break-word", margin: 0 }}>
                      {selectedLead.siteVisit}
                    </p>
                  </Col>

                  <Col span={6} className="col-padding" style={{ minWidth: 0 }}>
                    <strong>Looking For</strong>
                    <p style={{ wordBreak: "break-word", margin: 0 }}>
                      {selectedLead.lookingFor}
                    </p>
                  </Col>
                </Row>
                {/* <Button
                  type="primary"
                  onClick={() => handleLeadCheck(selectedLead, true)}
                >
                  Buy Now
                </Button>
                 */}
                {/* NEW DYNAMIC CART BUTTON */}
                {(() => {
                  const isChecked = cartLeads.some(
                    (l) => l.id === selectedLead.id,
                  );

                  return (
                    <Row justify="start" style={{ marginTop: 16 }}>
                      <Col>
                        <Button
                          size="middle"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLeadCheck(selectedLead, !isChecked);
                          }}
                          onMouseEnter={() => setIsHoveredDetail(true)}
                          onMouseLeave={() => setIsHoveredDetail(false)}
                          icon={
                            isChecked ? (
                              isHoveredDetail ? (
                                <DeleteOutlined style={{ fontSize: 13 }} />
                              ) : (
                                <CheckOutlined style={{ fontSize: 13 }} />
                              )
                            ) : (
                              <ShoppingCartOutlined style={{ fontSize: 14 }} />
                            )
                          }
                          style={{
                            fontWeight: 600,
                            fontSize: 12,
                            borderRadius: 6,
                            padding: "0 16px",
                            height: "32px",
                            textTransform: "uppercase",
                            transition: "all 0.2s ease",
                            backgroundColor: isChecked
                              ? isHoveredDetail
                                ? "#ff4d4f"
                                : "#1677ff"
                              : isHoveredDetail
                                ? "#1677ff"
                                : "#ffffff",
                            color:
                              isChecked || isHoveredDetail
                                ? "#ffffff"
                                : "#1677ff",
                            borderColor: isChecked
                              ? isHoveredDetail
                                ? "#ff4d4f"
                                : "#1677ff"
                              : "#1677ff",
                            boxShadow:
                              isChecked || isHoveredDetail
                                ? "0 2px 6px rgba(22, 119, 255, 0.3)"
                                : "0 2px 4px rgba(0,0,0,0.05)",
                          }}
                        >
                          {isChecked
                            ? isHoveredDetail
                              ? "Remove"
                              : "Added"
                            : "Add"}
                        </Button>
                      </Col>
                    </Row>
                  );
                })()}
              </Card>
            </>
          ) : (
            <AdminCard
              {...adminLead}
              onTopPickClick={(leadId) =>
                navigate(`/user-dashboard?selectedleadid=${leadId}`)
              }
              // onBuyNow={(l) => handleLeadCheck(l, true)}
            />
          )}

          {/* Subcategory Scroll */}
          <div
            className="thin-scroll"
            style={{ display: "flex", gap: "8px", overflowX: "auto" }}
          >
            {sortedSubCategories.map((sc) => {
              const leads = subcategoryLeadsMap[sc.value] || [];
              const loading = subcategoryLoading[sc.value];

              return (
                <div
                  key={sc.value}
                  className="thin-scroll"
                  style={{
                    minWidth: 240,
                    maxHeight: 360,
                    padding: "0 8px 8px",
                    background: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: 6,
                    overflowY: "auto",
                  }}
                >
                  <h5
                    style={{
                      margin: 0,
                      marginBottom: 2,
                      position: "sticky",
                      top: 0,
                      background: "#f9fafb",
                      zIndex: 2,
                      padding: "16px 8px 8px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    {sc.label} ({leads.length})
                  </h5>

                  {loading ? (
                    <Spin size="small" />
                  ) : leads.length === 0 ? (
                    <Typography.Text type="secondary">
                      No leads available
                    </Typography.Text>
                  ) : (
                    leads.map((l) => (
                      <LeadCard
                        key={l.id}
                        {...l}
                        onClick={() => setSelectedLead(l)}
                        showCartIcon={false}
                      />
                    ))
                  )}
                </div>
              );
            })}
          </div>
        </Col>
      </Row>

      {/* Floating Cart */}
      {cartLeads.length > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: 20,
            left: rightColPos.left,
            width: rightColPos.width,
            background: "#fff",
            border: "1px solid #1890ff",
            borderRadius: 8,
            padding: "12px",
            display: "flex",
            justifyContent: "space-between",
            zIndex: 1000,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ color: "#007bff", fontSize: 16, fontWeight: 600 }}>
              No. of {cartLeads.length === 1 ? "lead" : "leads"} Selected:{" "}
              {cartLeads.length}
            </span>
            <span style={{ color: "#555", fontSize: 13 }}>
              * Lead amount may change while someone purchases the same lead at
              a time
            </span>
          </div>
          <Button type="primary" onClick={() => navigate("/my-cart")}>
            Buy Now
          </Button>
        </div>
      )}
    </div>
  );
};

export default NewUserDashboardWeb;
